import { useCallback, useEffect, useRef, useState } from "react";

/** Dwell time before a panel opens, so a pointer sweeping the rail doesn't flash panels. */
const OPEN_DELAY_MS = 110;
/** Grace period after the pointer leaves, so it can cross the gap into the open panel. */
const CLOSE_DELAY_MS = 160;

export interface HoverIntent<TKey> {
  /** Key of the panel currently shown, or null when none is. */
  openKey: TKey | null;
  /** Open after the dwell delay; swapping while another panel is open is instant. */
  open: (key: TKey) => void;
  /** Open without waiting — keyboard focus and taps expect an immediate response. */
  openNow: (key: TKey) => void;
  /** Close after the grace period. */
  close: () => void;
  /** Close without waiting. */
  closeNow: () => void;
  /** Cancel a pending open or close — call when the pointer reaches the panel itself. */
  keepOpen: () => void;
}

/**
 * Hover-intent state for a set of triggers sharing one hover panel: opening waits for the
 * pointer to settle, closing waits long enough for the pointer to travel from trigger to
 * panel, and moving between triggers swaps the panel with no delay at all.
 */
export function useHoverIntent<TKey>(): HoverIntent<TKey> {
  const [openKey, setOpenKey] = useState<TKey | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOpen = useRef(false);

  const cancelPending = useCallback(() => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const commit = useCallback((key: TKey | null) => {
    isOpen.current = key !== null;
    setOpenKey(key);
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  const openNow = useCallback(
    (key: TKey) => {
      cancelPending();
      commit(key);
    },
    [cancelPending, commit],
  );

  const open = useCallback(
    (key: TKey) => {
      cancelPending();
      if (isOpen.current) {
        commit(key);
        return;
      }
      timer.current = setTimeout(() => commit(key), OPEN_DELAY_MS);
    },
    [cancelPending, commit],
  );

  const closeNow = useCallback(() => {
    cancelPending();
    commit(null);
  }, [cancelPending, commit]);

  const close = useCallback(() => {
    cancelPending();
    timer.current = setTimeout(() => commit(null), CLOSE_DELAY_MS);
  }, [cancelPending, commit]);

  return { openKey, open, openNow, close, closeNow, keepOpen: cancelPending };
}
